/**
 * PRISM Dashboard - Redesigned for Actionable Insights
 *
 * Shows real data only - no mock/fake metrics.
 * Focus on: What needs attention? What can I do next?
 */

import React, { useState, useEffect } from 'react';
import { useIsViewer } from '../hooks/useDemoViewer';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Skeleton,
  Divider,
  LinearProgress,
  Avatar,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  FolderOpen,
  Inventory2,
  Description,
  Warning,
  CheckCircle,
  ArrowForward,
  Add,
  Refresh,
  TrendingUp,
  PlayArrow,
  Download,
  Assignment,
  Business,
  Speed,
  Inventory,
  Assessment,
  Shield,
  VerifiedUser,
} from '@mui/icons-material';
import IrisIcon from './icons/IrisIcon';
import { useNavigate } from 'react-router-dom';
import { unifiedProjectsAPI, compliancePackagesAPI } from '../services/api';
import type { UnifiedProject } from '../types/unifiedProject';

interface DashboardMetrics {
  totalProjects: number;
  activeProjects: number;
  totalPackages: number;
  packagesReady: number;
  packagesInProgress: number;
  avgReadiness: number;
}

interface PackageSummary {
  projectId: number;
  projectName: string;
  packageKey: string;
  packageName: string;
  status: string;
  readinessScore: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const isViewer = useIsViewer();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<UnifiedProject[]>([]);
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalProjects: 0,
    activeProjects: 0,
    totalPackages: 0,
    packagesReady: 0,
    packagesInProgress: 0,
    avgReadiness: 0,
  });
  const [packageSummaries, setPackageSummaries] = useState<PackageSummary[]>([]);
  const [needsAttention, setNeedsAttention] = useState<PackageSummary[]>([]);
  const [availablePackageTypes, setAvailablePackageTypes] = useState<number>(0);
  const [packageTypesList, setPackageTypesList] = useState<Array<{ name: string; color: string }>>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Get all projects
      const projectData = await unifiedProjectsAPI.getAll();
      setProjects(projectData);

      // Get package types count
      const packageTypes = await compliancePackagesAPI.getPackageTypes();
      setAvailablePackageTypes(packageTypes.length);
      setPackageTypesList(packageTypes.filter(t => t.is_active).map(t => ({ name: t.name, color: t.color || '#4F46E5' })));

      // Aggregate package data across all projects
      const allPackages: PackageSummary[] = [];
      let totalReadiness = 0;
      let readinessCount = 0;

      // Fetch packages for each project (limit to first 10 for performance)
      const projectsToCheck = projectData.slice(0, 10);

      for (const project of projectsToCheck) {
        try {
          const { packages } = await compliancePackagesAPI.getProjectPackages(project.id);

          for (const pkg of packages) {
            allPackages.push({
              projectId: project.id,
              projectName: project.name,
              packageKey: pkg.package_key || '',
              packageName: pkg.type_name || pkg.package_key || 'Unknown',
              status: pkg.status,
              readinessScore: pkg.readiness_score || 0,
            });

            if (pkg.readiness_score !== undefined) {
              totalReadiness += pkg.readiness_score;
              readinessCount++;
            }
          }
        } catch (err) {
          // Project may not have packages, skip
        }
      }

      // Calculate metrics
      const packagesReady = allPackages.filter(p => p.status === 'ready' || p.status === 'exported').length;
      const packagesInProgress = allPackages.filter(p => p.status === 'generating' || p.status === 'draft').length;

      setMetrics({
        totalProjects: projectData.length,
        activeProjects: projectData.filter(p => p.status === 'active').length,
        totalPackages: allPackages.length,
        packagesReady,
        packagesInProgress,
        avgReadiness: readinessCount > 0 ? Math.round(totalReadiness / readinessCount) : 0,
      });

      setPackageSummaries(allPackages);

      // Identify items needing attention (low readiness, draft status)
      const attention = allPackages
        .filter(p => p.status === 'draft' || (p.readinessScore > 0 && p.readinessScore < 50))
        .sort((a, b) => a.readinessScore - b.readinessScore)
        .slice(0, 5);
      setNeedsAttention(attention);

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready':
      case 'exported': return 'success';
      case 'generating': return 'info';
      case 'draft': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getReadinessColor = (score: number) => {
    if (score >= 80) return '#10B981';
    if (score >= 50) return '#F59E0B';
    return '#EF4444';
  };

  // Quick Actions
  const quickActions = [
    ...(!isViewer ? [{
      title: 'New Project',
      description: 'Start a new security assessment',
      icon: <Add />,
      color: '#1E3A8A',
      onClick: () => navigate('/projects-v2/new'),
    }] : []),
    {
      title: 'Start Package',
      description: 'Begin a compliance package',
      icon: <Inventory2 />,
      color: '#059669',
      onClick: () => {
        if (projects.length > 0) {
          navigate(`/projects-v2/${projects[0].id}/packages`);
        } else {
          navigate('/projects-v2');
        }
      },
    },
    {
      title: 'Import Data',
      description: 'CSV, OSCAL, or GRC import',
      icon: <Download />,
      color: '#7C3AED',
      onClick: () => navigate('/projects-v2'),
    },
    {
      title: 'View Standards',
      description: `${availablePackageTypes} package types available`,
      icon: <VerifiedUser />,
      color: '#DC2626',
      onClick: () => navigate('/projects-v2'),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your compliance and security assessment overview
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadDashboardData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', borderLeft: '4px solid #1E3A8A' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar aria-hidden="true" sx={{ bgcolor: '#1E3A8A', width: 48, height: 48 }}>
                  <FolderOpen />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {loading ? <Skeleton width={40} /> : metrics.totalProjects}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Projects
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {metrics.activeProjects} active
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', borderLeft: '4px solid #059669' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar aria-hidden="true" sx={{ bgcolor: '#059669', width: 48, height: 48 }}>
                  <Inventory2 />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {loading ? <Skeleton width={40} /> : metrics.totalPackages}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Compliance Packages
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {metrics.packagesReady} ready to export
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', borderLeft: '4px solid #F59E0B' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar aria-hidden="true" sx={{ bgcolor: '#F59E0B', width: 48, height: 48 }}>
                  <Speed />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {loading ? <Skeleton width={40} /> : `${metrics.avgReadiness}%`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Readiness
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Across all packages
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', borderLeft: '4px solid #7C3AED' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar aria-hidden="true" sx={{ bgcolor: '#7C3AED', width: 48, height: 48 }}>
                  <Inventory />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {loading ? <Skeleton width={40} /> : availablePackageTypes}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Package Types
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Available frameworks
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Quick Actions
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {quickActions.map((action, index) => (
                <Grid item xs={6} key={index}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: 3,
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={action.onClick}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: action.color,
                          width: 40,
                          height: 40,
                          mx: 'auto',
                          mb: 1,
                        }}
                      >
                        {action.icon}
                      </Avatar>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {action.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {action.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Needs Attention */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Warning color="warning" />
              <Typography variant="h6" fontWeight="bold">
                Needs Attention
              </Typography>
            </Box>

            {loading ? (
              <Box>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} height={60} sx={{ mb: 1 }} />
                ))}
              </Box>
            ) : needsAttention.length > 0 ? (
              <List dense>
                {needsAttention.map((item, index) => (
                  <ListItem
                    key={`${item.projectId}-${item.packageKey}`}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': { bgcolor: 'action.hover', cursor: 'pointer' },
                    }}
                    onClick={() => navigate(`/projects-v2/${item.projectId}/packages`)}
                  >
                    <ListItemIcon>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: getReadinessColor(item.readinessScore),
                          fontSize: '0.75rem',
                        }}
                      >
                        {item.readinessScore}%
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={item.packageName}
                      secondary={item.projectName}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        label={item.status}
                        size="small"
                        color={getStatusColor(item.status) as any}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  All packages are in good shape!
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Recent Projects */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Recent Projects
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/projects-v2')}
              >
                View All
              </Button>
            </Box>

            {loading ? (
              <Box>
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} height={70} sx={{ mb: 2 }} />
                ))}
              </Box>
            ) : projects.length > 0 ? (
              <List>
                {projects.slice(0, 5).map((project) => (
                  <ListItem
                    key={project.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      mb: 1.5,
                      '&:hover': { bgcolor: 'action.hover', cursor: 'pointer' },
                    }}
                    onClick={() => navigate(`/projects-v2/${project.id}`)}
                  >
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <Business />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {project.name}
                          </Typography>
                          <Chip
                            label={project.status}
                            size="small"
                            color={project.status === 'active' ? 'success' : 'default'}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                          {project.client_name && (
                            <Typography variant="caption" color="text.secondary">
                              {project.client_name}
                            </Typography>
                          )}
                          {project.industry_sector && (
                            <Typography variant="caption" color="text.secondary">
                              {project.industry_sector}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {project.asset_count || 0} assets
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => navigate(`/projects-v2/${project.id}`)} aria-label="View project">
                        <ArrowForward />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <FolderOpen sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                  {isViewer ? 'No projects in this demo' : 'No projects yet'}
                </Typography>
                {!isViewer && (
                  <Button
                    variant="contained"
                    startIcon={<IrisIcon />}
                    onClick={() => navigate('/projects-v2/new')}
                    sx={{ mt: 1 }}
                  >
                    Create Your First Project
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Package Types Overview */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Available Packages
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {availablePackageTypes} compliance frameworks ready for your projects
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 280, overflowY: 'auto' }}>
              {packageTypesList.map((pkg) => (
                <Box key={pkg.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: pkg.color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2">
                    {pkg.name}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Button
              fullWidth
              variant="outlined"
              sx={{ mt: 3 }}
              onClick={() => projects.length > 0 ? navigate(`/projects-v2/${projects[0].id}/packages`) : navigate('/projects-v2')}
            >
              Start a Package
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
